import { Component, EventEmitter, effect, inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { ApiService } from '@app/core/services/API/api-service';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { ToastrService } from 'ngx-toastr';
import { Spinner } from '../spinner/spinner';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { UserSettingsService } from '@app/shared/services/user-settings.service';


export interface NavigationData {
  name: string;
  path: string;
}

export interface NavigationDetails {
  _id: string;
  navigationData: NavigationData[];
  createdAt: string;
  status: string;
}

export interface MailTemplate {
  _id: string;
  name: string;
  notificationName: string;
  content: string;
  isRequired: boolean;
  status: string;
  excludedUsers: string[];
  updatedAt: string;
  isDisabled:string
}

export interface UserSettings {
  firstName: string;
  lastName: string;
  email?: string;
  title: string;
  aboutMe: string;
  phone: string;
  ext: string;
  mobile: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  defaultPage: string;
  emailNotifications: boolean;
  emailTime: string;
  isActive: boolean;
  accessLevel: string;
  profilePhoto?: string;
  redirectTo?: string;
  name?: string; // Keep for backward compatibility
  job?: string; // Keep for backward compatibility
  mailTemplates?: MailTemplate[];
  disabledMailTemplateIDs?: string[];
}

@Component({
  selector: 'app-my-settings',
  imports: [Spinner, FormsModule, ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './my-settings.html',
  styleUrl: './my-settings.scss'
})
export class MySettings implements OnInit, OnDestroy {

  @Output() close = new EventEmitter<void>();
  @Output() settingsSaved = new EventEmitter<UserSettings>();

  // Form data
  settings = signal<UserSettings>({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    aboutMe: '',
    phone: '',
    ext: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    defaultPage: 'my-work',
    redirectTo: '',
    emailNotifications: true,
    emailTime: '07:00',
    isActive: true,
    accessLevel: 'client-access',
    profilePhoto: '',
    mailTemplates: [],
    disabledMailTemplateIDs: []
  });

  // Store initial state to revert on cancel
  private initialSettings: UserSettings = {
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    aboutMe: '',
    phone: '',
    ext: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    defaultPage: 'my-work',
    redirectTo: '',
    emailNotifications: true,
    emailTime: '07:00',
    isActive: true,
    accessLevel: 'client-access',
    profilePhoto: '',
    mailTemplates: [],
    disabledMailTemplateIDs: []
  };

  // Track which fields have been changed
  private changedFields = new Set<string>();
  private trackedSettingsUserId: string | null = null;

  readonly settingsForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl(''),
    email: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.email]),
    title: new FormControl('', Validators.required),
    phone: new FormControl('', Validators.required),
    address: new FormControl(''),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    postalCode: new FormControl(''),
    country: new FormControl(''),
    defaultPage: new FormControl(''),
    accessLevel: new FormControl(''),
  });

  // Loading state for API operations
  isSaving = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  // Accordion state - track which sections are expanded
  expandedSections = signal<Set<string>>(new Set(['personal-info'])); // First section expanded by default

  /** Maps required/validated form controls to their accordion section ids */
  private readonly fieldSectionMap: Record<string, string> = {
    firstName: 'personal-info',
    lastName: 'personal-info',
    email: 'personal-info',
    title: 'personal-info',
    phone: 'contact-info',
    address: 'contact-info',
    city: 'contact-info',
    state: 'contact-info',
    postalCode: 'contact-info',
    country: 'contact-info',
    defaultPage: 'preferences',
  };

  // Track mouse drag state to prevent modal close on text selection
  private mouseDownPosition: { x: number; y: number } | null = null;
  private isDragging = false;

  private readonly toastr = inject(ToastrService);

  // Dropdown options - now dynamic based on API response
  defaultPageOptions = signal<Array<{ value: string; label: string }>>([

  ]);


  emailTimeOptions = [
    { value: '06:00', label: '6:00 AM' },
    { value: '07:00', label: '7:00 AM' },
    { value: '08:00', label: '8:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '13:00', label: '1:00 PM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' },
    { value: '17:00', label: '5:00 PM' },
    { value: '18:00', label: '6:00 PM' }
  ];

  accessLevelOptions = [
    { value: 'client-access', label: 'Client Access' },
    { value: 'admin-access', label: 'Admin Access' },
    { value: 'manager-access', label: 'Manager Access' },
    { value: 'developer-access', label: 'Developer Access' },
    { value: 'viewer-access', label: 'Viewer Access' }
  ];

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private userSettingsService: UserSettingsService
  ) {
    effect(() => {
      const userId = this.authService.userData()?._id ?? null;
      if (!userId) {
        return;
      }

      if (this.trackedSettingsUserId === null) {
        this.trackedSettingsUserId = userId;
        return;
      }

      if (userId === this.trackedSettingsUserId) {
        return;
      }

      this.trackedSettingsUserId = userId;
      this.changedFields.clear();
      this.userSettingsService.clearSettings();
      this.applySettingsFromServiceOrLoad();
    });
  }

  ngOnInit() {
    this.trackedSettingsUserId =
      this.authService.userData()?._id ?? localStorage.getItem('userID');
    this.setupFormValueSync();
    this.applySettingsFromServiceOrLoad();
    this.changedFields.clear();
  }

  private applySettingsFromServiceOrLoad(): void {
    if (this.userSettingsService.areSettingsLoaded()) {
      const preloadedSettings = this.userSettingsService.getCurrentSettings();
      const preloadedNavigationOptions = this.userSettingsService.getCurrentNavigationOptions();

      if (preloadedSettings) {
        this.applyLoadedSettings(preloadedSettings, preloadedNavigationOptions);
        return;
      }
    }

    this.loadUserSettings();
  }

  private applyLoadedSettings(
    preloadedSettings: UserSettings,
    preloadedNavigationOptions: Array<{ value: string; label: string }>
  ): void {
    this.settings.set(preloadedSettings);
    this.defaultPageOptions.set(preloadedNavigationOptions);
    this.initialSettings = { ...preloadedSettings };
    this.patchFormFromSettings(preloadedSettings);
    this.isLoading.set(false);
  }

  ngOnDestroy() {
    // Component cleanup
  }



  private loadUserSettings(): void {
    this.isLoading.set(true);

    // Use the service to load settings
    this.userSettingsService.loadUserSettings().subscribe({
      next: (response) => {
        console.log('User settings loaded:', response);
        this.userSettingsService.processUserSettings(response);
        
        // Get the processed settings from service
        const processedSettings = this.userSettingsService.getCurrentSettings();
        const navigationOptions = this.userSettingsService.getCurrentNavigationOptions();
        
        if (processedSettings) {
          this.applyLoadedSettings(processedSettings, navigationOptions);
        }
        
        // Reset changed fields tracking after loading
        this.changedFields.clear();
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading user settings:', error);
        this.toastr.error('Failed to load user settings. Please try again.');
        
        // Set default values to prevent empty form
        this.settings.set({
          ...this.settings(),
          firstName: localStorage.getItem('userName')?.split(' ')[0] || '',
          lastName: localStorage.getItem('userName')?.split(' ').slice(1).join(' ') || '',
          email: localStorage.getItem('userEmail') || '',
        });
        this.patchFormFromSettings(this.settings());
        
        this.isLoading.set(false);
      }
    });
  }


  onClose() {
    // Reset drag state first
    this.isDragging = false;
    this.mouseDownPosition = null;
    
    // Check if text is being selected
    const selection = window.getSelection();
    const hasSelection = selection ? selection.toString().length > 0 : false;
    
    // Only prevent close if actively selecting text, otherwise allow close
    if (!hasSelection) {
      this.close.emit();
    }
  }

  onOverlayMouseDown(event: MouseEvent) {
    // Don't track drag if clicking on interactive elements
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('textarea') || target.closest('select') || target.closest('.ng-select') || target.closest('.modal-actions')) {
      return;
    }
    
    // Track the initial mouse position
    this.mouseDownPosition = {
      x: event.clientX,
      y: event.clientY
    };
    this.isDragging = false;
  }

  onOverlayMouseMove(event: MouseEvent) {
    // Don't track drag if interacting with form elements
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('textarea') || target.closest('select') || target.closest('.ng-select') || target.closest('.modal-actions')) {
      return;
    }
    
    // If mouse is down and moved, it's a drag
    if (this.mouseDownPosition) {
      const deltaX = Math.abs(event.clientX - this.mouseDownPosition.x);
      const deltaY = Math.abs(event.clientY - this.mouseDownPosition.y);
      // If moved more than 5 pixels, consider it a drag
      if (deltaX > 5 || deltaY > 5) {
        this.isDragging = true;
      }
    }
  }

  onOverlayMouseUp() {
    // Check if text is being selected
    const selection = window.getSelection();
    const hasSelection = selection ? selection.toString().length > 0 : false;
    if (hasSelection) {
      this.isDragging = true; // Treat text selection as a drag
    }
    // Reset drag tracking after a short delay to allow click event to check isDragging
    setTimeout(() => {
      this.isDragging = false;
      this.mouseDownPosition = null;
    }, 100);
  }

  /**
   * Reset drag state - called by button mousedown events to ensure buttons always work
   */
  resetDragState(): void {
    this.isDragging = false;
    this.mouseDownPosition = null;
  }

  /**
   * Check if there are any changes to save
   */
  private hasChanges(): boolean {
    return this.changedFields.size > 0;
  }

  /**
   * Get the number of changed fields (for UI display)
   */
  getChangedFieldsCount(): number {
    return this.changedFields.size;
  }

  /**
   * Get only the changed fields for API payload
   */
  private getChangedFieldsPayload(): any {
    const currentSettings = this.settings();
    const payload: any = {};

    // Only include changed fields in the payload
    if (this.changedFields.has('firstName') || this.changedFields.has('lastName')) {
      const fullName = `${currentSettings.firstName} ${currentSettings.lastName}`.trim();
      payload.name = fullName;
      payload.firstName = currentSettings.firstName;
      payload.lastName = currentSettings.lastName;
    }

    if (this.changedFields.has('email')) {
      payload.email = currentSettings.email;
    }

    if (this.changedFields.has('address')) {
      payload.address = currentSettings.address;
    }

    if (this.changedFields.has('phone')) {
      payload.phone = currentSettings.phone;
    }

    if (this.changedFields.has('mobile')) {
      payload.mobile = currentSettings.mobile;
    }

    if (this.changedFields.has('city')) {
      payload.city = currentSettings.city;
    }

    if (this.changedFields.has('state')) {
      payload.state = currentSettings.state;
    }

    if (this.changedFields.has('postalCode')) {
      payload.postalCode = currentSettings.postalCode;
    }

    if (this.changedFields.has('country')) {
      payload.country = currentSettings.country;
    }

    if (this.changedFields.has('emailNotifications')) {
      payload.emailNotifications = currentSettings.emailNotifications;
    }

    if (this.changedFields.has('isActive')) {
      payload.isActive = currentSettings.isActive;
    }

    // if (this.changedFields.has('accessLevel')) {
    //   payload.accessLevel = currentSettings.accessLevel;
    // }

    if (this.changedFields.has('profilePhoto')) {
      payload.profilePhoto = currentSettings.profilePhoto;
    }

    if (this.changedFields.has('title')) {
      payload.jobInfo = currentSettings.title;
    }

    return payload;
  }

  onSave() {
    // Reset drag state to ensure save always works
    this.isDragging = false;
    this.mouseDownPosition = null;
    
    // Check if there are any changes to save
    if (!this.hasChanges()) {
      this.toastr.info('No changes to save');
      this.closeModal();
      return;
    }

    // Validate form
    if (!this.validateForm()) {
      return;
    }

    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.warn('User not authenticated, saving to localStorage only');
      this.saveToLocalStorage();
      this.closeModal();
      return;
    }

    // Show loading state
    this.isSaving.set(true);
    console.log('Saving settings to API...');

    // Get user ID
    const userId = localStorage.getItem('userID');
    if (!userId) {
      this.toastr.error('User ID not found');
      this.isSaving.set(false);
      return;
    }

    // Prepare settings data - only changed fields
    const currentSettings = this.settings();
    const changedUserDetails = this.getChangedFieldsPayload();
    
    // Calculate enabled and disabled template IDs only if mail templates were changed
    let disableMailTemplateID: string[] = [];
    let enableMailTemplateID: string[] = [];
    
    if (this.changedFields.has('disabledMailTemplateIDs')) {
      const allTemplateIds = currentSettings.mailTemplates?.map(template => template._id) || [];
      const disabledTemplateIds = currentSettings.disabledMailTemplateIDs || [];
      disableMailTemplateID = disabledTemplateIds;
      enableMailTemplateID = allTemplateIds.filter(id => !disabledTemplateIds.includes(id));
    }
    
    // Only include navigation path if defaultPage was changed
    let navigationPath = undefined;
    if (this.changedFields.has('defaultPage')) {
      navigationPath = {
        path: `${currentSettings.defaultPage}`
      };
    }
    
    const apiPayload: any = {};
    
    // Only include userDetails if there are user detail changes
    if (Object.keys(changedUserDetails).length > 0) {
      apiPayload.userDetails = changedUserDetails;
    }
    
    // Only include navigationPath if it was changed
    if (navigationPath) {
      apiPayload.navigationPath = navigationPath;
    }
    
    // Only include mail template changes if they were changed
    if (disableMailTemplateID.length > 0 || enableMailTemplateID.length > 0) {
      apiPayload.disableMailTemplateID = disableMailTemplateID;
      apiPayload.enableMailTemplateID = enableMailTemplateID;
    }

    console.log('API Payload (only changed fields):', apiPayload);
    console.log('Changed fields:', Array.from(this.changedFields));
    console.log('Disabled Mail Template IDs:', disableMailTemplateID);
    console.log('Enabled Mail Template IDs:', enableMailTemplateID);

    // Save to API (assuming there's an update user profile endpoint)
    this.apiService.updateSettings( apiPayload).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.toastr.success('Settings saved successfully!');
          
          // Reset changed fields tracking after successful save
          this.changedFields.clear();
          
          // Update initial settings to current settings
          this.initialSettings = { ...this.settings() };
          
          this.loadUserSettings();
          if (apiPayload.userDetails?.name) {
            localStorage.setItem('userName', apiPayload.userDetails.name);
          }
          // Emit event for parent components
          this.settingsSaved.emit(this.settings());
        } else {
          this.toastr.error('Failed to save settings. Please try again.');
        }
      },
      error: (error: any) => {
        console.error('Error saving settings to API:', error);
        this.toastr.error('Network error. Settings saved locally only.');
        
        // Fallback to localStorage
        this.saveToLocalStorage();
      },
      complete: () => {
        // Reset loading state
        this.isSaving.set(false);
        
        // Close modal
        this.closeModal();
      }
    });
  }

  private validateForm(): boolean {
    this.syncSettingsFromForm();
    this.settingsForm.markAllAsTouched();

    if (this.settingsForm.invalid) {
      this.expandSectionsWithErrors();
      this.toastr.error('Please fill in all required fields');
      this.scrollToFirstInvalidField();
      return false;
    }

    return true;
  }

  /**
   * Expand every accordion section that contains an invalid control
   * so validation messages are visible after Save.
   */
  private expandSectionsWithErrors(): void {
    const sectionsToExpand = new Set(this.expandedSections());

    for (const [controlName, sectionId] of Object.entries(this.fieldSectionMap)) {
      const control = this.settingsForm.get(controlName);
      if (control?.invalid) {
        sectionsToExpand.add(sectionId);
      }
    }

    this.expandedSections.set(sectionsToExpand);
  }

  /** Focus / scroll to the first invalid field after sections expand */
  private scrollToFirstInvalidField(): void {
    // Wait for accordion expand transition so the field is in the layout
    setTimeout(() => {
      const firstInvalid = document.querySelector(
        '.settings-form input.ng-invalid.ng-touched, .settings-form textarea.ng-invalid.ng-touched, .settings-form select.ng-invalid.ng-touched'
      ) as HTMLElement | null;

      if (!firstInvalid) {
        return;
      }

      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof firstInvalid.focus === 'function') {
        firstInvalid.focus({ preventScroll: true });
      }
    }, 320);
  }

  /** True when a section has at least one invalid, touched control */
  sectionHasErrors(sectionId: string): boolean {
    return Object.entries(this.fieldSectionMap).some(([controlName, mappedSection]) => {
      if (mappedSection !== sectionId) {
        return false;
      }
      const control = this.settingsForm.get(controlName);
      return !!(control && control.invalid && control.touched);
    });
  }

  showFieldError(controlName: string): boolean {
    const control = this.settingsForm.get(controlName);
    return !!(control && control.invalid && control.touched);
  }

  getFieldError(controlName: string): string {
    const control = this.settingsForm.get(controlName);
    if (!control?.errors || !control.touched) {
      return '';
    }

    if (control.errors['required']) {
      switch (controlName) {
        case 'firstName':
          return 'Please enter the First Name';
        case 'email':
          return 'Please enter the Email';
        case 'title':
          return 'Please enter Job Title';
        case 'phone':
          return 'Please enter the Phone number';
        case 'city':
          return 'Please enter City';
        case 'state':
          return 'Please enter State';
        default:
          return 'This field is required';
      }
    }

    if (control.errors['email']) {
      return 'Please enter a valid Email';
    }

    return '';
  }

  private patchFormFromSettings(settings: UserSettings): void {
    this.settingsForm.patchValue(
      {
        firstName: settings.firstName ?? '',
        lastName: settings.lastName ?? '',
        email: settings.email ?? '',
        title: settings.title ?? '',
        phone: settings.phone ?? '',
        address: settings.address ?? '',
        city: settings.city ?? '',
        state: settings.state ?? '',
        postalCode: settings.postalCode ?? '',
        country: settings.country ?? '',
        defaultPage: settings.redirectTo || settings.defaultPage || '',
        accessLevel: settings.accessLevel ?? '',
      },
      { emitEvent: false }
    );
  }

  private setupFormValueSync(): void {
    this.settingsForm.valueChanges.subscribe(() => {
      this.syncSettingsFromForm();
    });
  }

  private syncSettingsFromForm(): void {
    const raw = this.settingsForm.getRawValue();
    const defaultPage = raw.defaultPage ?? '';
    const nextSettings: UserSettings = {
      ...this.settings(),
      firstName: raw.firstName ?? '',
      lastName: raw.lastName ?? '',
      email: raw.email ?? this.settings().email ?? '',
      title: raw.title ?? '',
      phone: raw.phone ?? '',
      address: raw.address ?? '',
      city: raw.city ?? '',
      state: raw.state ?? '',
      postalCode: raw.postalCode ?? '',
      country: raw.country ?? '',
      defaultPage,
      redirectTo: defaultPage || this.settings().redirectTo || '',
      accessLevel: raw.accessLevel ?? '',
    };

    this.settings.set(nextSettings);
    this.trackChangedFields(nextSettings);
  }

  private trackChangedFields(current: UserSettings): void {
    this.changedFields.clear();

    const fieldsToTrack: (keyof UserSettings)[] = [
      'firstName',
      'lastName',
      'email',
      'title',
      'phone',
      'address',
      'city',
      'state',
      'postalCode',
      'country',
      'defaultPage',
      // 'accessLevel',
    ];

    for (const field of fieldsToTrack) {
      const currentValue = current[field];
      const initialValue = this.initialSettings[field];
      if (String(currentValue ?? '') !== String(initialValue ?? '')) {
        this.changedFields.add(field);
      }
    }
  }

  private saveToLocalStorage(): void {
    // Fallback: save to localStorage only
    const currentSettings = this.settings();
    
    // Create full name from firstName and lastName
    const fullName = `${currentSettings.firstName} ${currentSettings.lastName}`.trim();
    
    if (fullName) {
      localStorage.setItem('userName', fullName);
    }
    
    // Store other settings in localStorage
    localStorage.setItem('userSettings', JSON.stringify(currentSettings));
    
    // Reset changed fields tracking after saving to localStorage
    this.changedFields.clear();
    
    this.settingsSaved.emit(currentSettings);
  }

  private closeModal(): void {
    // Close modal
    this.close.emit();
  }

  onCancel() {
    // Reset drag state to ensure cancel always works
    this.isDragging = false;
    this.mouseDownPosition = null;
    
    // Revert to initial state
    this.settings.set({ ...this.initialSettings });
    this.patchFormFromSettings(this.initialSettings);
    
    // Reset changed fields tracking
    this.changedFields.clear();
    
    this.close.emit();
  }

  /**
   * Check if a template is disabled
   */
  isTemplateDisabled(templateId: string): boolean {
    const currentSettings = this.settings();
    return currentSettings.disabledMailTemplateIDs?.includes(templateId) || false;
  }

  /**
   * Handle template toggle
   */
  onTemplateToggle(templateId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isEnabled = target.checked;
    
    console.log('Template toggle:', { templateId, isEnabled });
    
    const currentSettings = this.settings();
    let disabledIds = [...(currentSettings.disabledMailTemplateIDs || [])];
    
    if (isEnabled) {
      // Remove from disabled list
      disabledIds = disabledIds.filter(id => id !== templateId);
    } else {
      // Add to disabled list
      if (!disabledIds.includes(templateId)) {
        disabledIds.push(templateId);
      }
    }
    
    console.log('Updated disabled IDs:', disabledIds);
    
    this.settings.set({
      ...currentSettings,
      disabledMailTemplateIDs: disabledIds
    });
    
    // Track mail template changes
    this.changedFields.add('disabledMailTemplateIDs');
  }

  getCurrentTheme(): 'light' | 'dark' {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }

  // Accordion functionality
  toggleSection(sectionId: string): void {
    const currentExpanded = this.expandedSections();
    const newExpanded = new Set(currentExpanded);
    
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    
    this.expandedSections.set(newExpanded);
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections().has(sectionId);
  }

  // Photo handling methods
  uploadPhoto(): void {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    fileInput?.click();
  }

  editPhoto(): void {
    this.uploadPhoto();
  }

  deletePhoto(): void {
    const currentSettings = this.settings();
    this.settings.set({
      ...currentSettings,
      profilePhoto: ''
    });
    // Get user ID
    const userId = localStorage.getItem('userID');
    if (!userId) {
      this.toastr.error('User ID not found');
      return;
    }
    this.changedFields.add('profilePhoto');
    this.apiService.profileImageDelete(userId).subscribe({
      next: (response: any) => {
        if (response.success) {
          // Update profile photo with the returned URL
          const currentSettings = this.settings();
          const newProfilePhoto =  '';
          this.settings.set({
            ...currentSettings,
            profilePhoto: newProfilePhoto
          });
          this.changedFields.add('profilePhoto');
          // Save to localStorage for immediate header update
          localStorage.setItem('userImage', newProfilePhoto);
          
          // Emit settings saved event to notify parent components
          this.settingsSaved.emit(this.settings());
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response.message || 'Failed to delete profile photo. Please try again.');
        }
      },
      error: (error: any) => {
        console.error('Error uploading profile photo:', error);
        this.toastr.error(error.message || 'Failed to delete profile photo. Please try again.');
        this.isSaving.set(false);
      },
     
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    console.log('Selected file:', file);
    
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/gif', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Please upload a JPG, GIF or PNG file');
      return;
    }

    // Validate file size (4MB limit)
    const maxSize = 4 * 1024 * 1024; // 4MB in bytes
    if (file.size > maxSize) {
      this.toastr.error('File size must be less than 4MB');
      return;
    }

    // Get user ID
    const userId = localStorage.getItem('userID');
    if (!userId) {
      this.toastr.error('User ID not found');
      return;
    }

    // Create FormData for API request
    const formData = new FormData();
    formData.append('file', file);

    // Show loading state
    this.isSaving.set(true);

    // Call API to upload image
    this.apiService.profileImageUpload(formData, userId).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          // Update profile photo with the returned URL
          const currentSettings = this.settings();
          console.log
          ('Image upload response:', response);
          const newProfilePhoto = response.data.image || '';
          this.settings.set({
            ...currentSettings,
            profilePhoto: newProfilePhoto
          });
          this.changedFields.add('profilePhoto');
          // Save to localStorage for immediate header update
          localStorage.setItem('userImage', newProfilePhoto);
          
          // Emit settings saved event to notify parent components
          this.settingsSaved.emit(this.settings());
          this.toastr.success(response.message||'Profile photo uploaded successfully');
        } else {
          this.toastr.error('Failed to upload profile photo. Please try again.');
        }
      },
      error: (error: any) => {
        console.error('Error uploading profile photo:', error);
        this.toastr.error('Failed to upload profile photo. Please try again.');
        this.isSaving.set(false);
      },
      complete: () => {
        this.isSaving.set(false);
        // Clear the file input
        input.value = '';
      }
    });
  }

}
